import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db } from './firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';

// --- 全てのページコンポーネントをインポート ---
import Login from './components/Login';
import Signup from './components/Signup';
import PasswordReset from './components/PasswordReset';
import PrivacyPolicy from './components/PrivacyPolicy';

import MainMenu from './components/MainMenu';

import ProfileSettings from './components/ProfileSettings';
import ShippingAddressList from './components/ShippingAddressList';
import SampleOrder from './components/SampleOrder';
import OrderConfirmation from './components/OrderConfirmation';
import OrderHistory from './components/OrderHistory';
import ContactForm from './components/ContactForm';
import ForgotPasswordPage from './components/ForgotPasswordPage';

import CustomerFollow from './components/CustomerFollow';
import EmployeeOrderSearch from './components/EmployeeOrderSearch';

import ShipperMenu from './components/ShipperMenu';
import OrderProcessing from './components/OrderProcessing';
import CsvImport from './components/CsvImport';
import ManualShipment from './components/ManualShipment';
import ShipperOrderHistory from './components/ShipperOrderHistory';
import History from './components/History';

import AdminMenu from './components/AdminMenu';
import NewsAdmin from './components/NewsAdmin';
import ProductAdmin from './components/ProductAdmin';
import UserAdmin from './components/userAdmin';
import SystemSettings from './components/SystemSettings';
import HolidayAdmin from './components/HolidayAdmin';
import AllOrderHistory from './components/AllOrderHistory';
import LoginLogViewer from './components/LoginLogViewer';
import PartNumberRanking from './components/PartNumberRanking';
import DeliveryEstimateAdmin from './components/DeliveryEstimateAdmin';
import LoginManagement from './components/LoginManagement';

// --- Context Providerをインポート ---
import { OrderProvider } from './components/OrderContext';

import { Spinner } from 'react-bootstrap';

/**
 * 各種Route保護コンポーネント（省略せず記述）
 */
function PrivateRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);
  if (loading) return <div className="d-flex vh-100 justify-content-center align-items-center"><Spinner animation="border" /></div>;
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function EmployeeRoute({ children }) {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !user.isAnonymous) {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userAttribute = userDocSnap.data().userAttribute;
          if (userAttribute === '社員' || userAttribute === '管理者') setIsAuthorized(true);
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);
  if (loading) return <div className="d-flex vh-100 justify-content-center align-items-center"><Spinner animation="border" /></div>;
  return isAuthorized ? children : <Navigate to="/mainmenu" />;
}

function ShipperRoute({ children }) {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !user.isAnonymous) {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userAttribute = userDocSnap.data().userAttribute;
          if (userAttribute === '出荷担当者' || userAttribute === '管理者') setIsAuthorized(true);
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);
  if (loading) return <div className="d-flex vh-100 justify-content-center align-items-center"><Spinner animation="border" /></div>;
  return isAuthorized ? children : <Navigate to="/mainmenu" />;
}

function AdminRoute({ children }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !user.isAnonymous) {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists() && userDocSnap.data().userAttribute === '管理者') setIsAdmin(true);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);
  if (loading) return <div className="d-flex vh-100 justify-content-center align-items-center"><Spinner animation="border" /></div>;
  return isAdmin ? children : <Navigate to="/mainmenu" />;
}

/**
 * アプリケーションのエントリーポイント
 */
function App() {
  // --- 管理者以外の強制ログアウト監視ロジック ---
  useEffect(() => {
    let unsubscribeSnapshot = null;

    // Snapshot監視を安全に停止するための関数
    const stopSnapshot = () => {
      if (unsubscribeSnapshot) {
        console.log("Cleanup: Stopping Firestore snapshot listener...");
        unsubscribeSnapshot();
        unsubscribeSnapshot = null; // 確実にnullを代入
      }
    };

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      // ユーザー状態が変わったら、何よりも先に監視を停止
      stopSnapshot();

      if (user) {
        try {
          // 1. 匿名ユーザーの場合
          if (user.isAnonymous) {
            setupMaintenanceListener();
            return;
          }

          // 2. 記名ユーザーの場合、属性を確認
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            // 管理者の場合は監視を登録せずに終了
            if (userData.userAttribute === '管理者') {
              return; 
            }
          }
          
          // 3. 管理者以外なら監視を開始
          setupMaintenanceListener();

        } catch (error) {
          console.error("User attribute fetch error:", error);
        }
      }
    });

    // 監視登録用の共通関数
    function setupMaintenanceListener() {
      // 重複登録を防ぐ
      if (unsubscribeSnapshot) return;

      unsubscribeSnapshot = onSnapshot(doc(db, "system_settings", "session_control"), (docSnap) => {
        if (docSnap.exists()) {
          // ★重要: signOutを呼ぶ「前」に監視を自ら停止する
          stopSnapshot();

          signOut(auth).then(() => {
            alert("システムメンテナンスのためログアウトされました。");
            window.location.href = "/login";
          }).catch(err => {
            console.error("SignOut Error:", err);
          });
        }
      }, (error) => {
        // ログアウト時の権限エラーをキャッチして内部エラーを防ぐ
        console.warn("Snapshot listener closed by authority change.");
      });
    }

    return () => {
      unsubscribeAuth();
      stopSnapshot();
    };
  }, []);

  return (
    <Router>
      <OrderProvider>
        <Routes>
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/reset-password" element={<PasswordReset />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          
          <Route path="/mainmenu" element={<PrivateRoute><MainMenu /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><ProfileSettings /></PrivateRoute>} />
          <Route path="/shipping-addresses" element={<PrivateRoute><ShippingAddressList /></PrivateRoute>} />
          <Route path="/contact" element={<PrivateRoute><ContactForm /></PrivateRoute>} />
          <Route path="/new-order" element={<PrivateRoute><SampleOrder /></PrivateRoute>} />
          <Route path="/order-confirmation" element={<PrivateRoute><OrderConfirmation /></PrivateRoute>} />
          <Route path="/order-history" element={<PrivateRoute><OrderHistory /></PrivateRoute>} />
          <Route path="/edit-order/:orderId" element={<PrivateRoute><SampleOrder /></PrivateRoute>} />
          
          <Route path="/customer-follow" element={<EmployeeRoute><CustomerFollow /></EmployeeRoute>} />
          <Route path="/employee/order-search" element={<EmployeeRoute><EmployeeOrderSearch /></EmployeeRoute>} />

          <Route path="/shipper-menu" element={<ShipperRoute><ShipperMenu /></ShipperRoute>} />
          <Route path="/shipper/order-processing" element={<ShipperRoute><OrderProcessing /></ShipperRoute>} />
          <Route path="/csv-import" element={<ShipperRoute><CsvImport /></ShipperRoute>} />
          <Route path="/manual-shipment" element={<ShipperRoute><ManualShipment /></ShipperRoute>} />
          <Route path="/shipper-all-history" element={<ShipperRoute><ShipperOrderHistory /></ShipperRoute>} />
          <Route path="/history" element={<AdminRoute><History /></AdminRoute>} />
          
          <Route path="/admin" element={<AdminRoute><AdminMenu /></AdminRoute>} />
          <Route path="/admin/login-management" element={<AdminRoute><LoginManagement /></AdminRoute>} />
          <Route path="/admin/news" element={<AdminRoute><NewsAdmin /></AdminRoute>} />
          <Route path="/admin/products" element={<AdminRoute><ProductAdmin /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><UserAdmin /></AdminRoute>} />
          <Route path="/admin/settings" element={<AdminRoute><SystemSettings /></AdminRoute>} />
          <Route path="/admin/holidays" element={<AdminRoute><HolidayAdmin /></AdminRoute>} />
          <Route path="/admin/all-orders" element={<AdminRoute><AllOrderHistory /></AdminRoute>} />
          <Route path="/admin/login-logs" element={<AdminRoute><LoginLogViewer /></AdminRoute>} />
          <Route path="/admin/ranking" element={<AdminRoute><PartNumberRanking /></AdminRoute>} />
          <Route path="/admin/delivery-estimate" element={<AdminRoute><DeliveryEstimateAdmin /></AdminRoute>} />
        </Routes>
      </OrderProvider>
    </Router>
  );
}

export default App;